import { Col, Container, Row } from '@geberit/gdds';

// styles
import styles from './highlight-teaser.module.scss';

// components
import { Headline, Formats, getFontWeight } from 'components/ContentElements/Headline';
import { Image } from 'components/image/image';
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { CmsLink } from 'components/Link/CmsLink';

// utils
import { useIsMobile } from 'components/App/SizeProvider';
import { classNameBuilder as buildClassName } from 'utils/classNameBuilder';
import { useThemeName } from 'utils/hooks/use-theme';
import { useSectionId } from 'utils/hooks/use-section-id';

export interface HighlightTeaserProps {
  title?: string;
  subtitle?: string;
  text?: string;
  colorVariant: string;
  link?: Link;
  link2?: Link;
  backgroundColor: string;
  pictureObject: ImageObject;
  pictureAlt: string;
  emphasize?: string;
  contentIndex?: number;
}

export function HighlightTeaser({
  title,
  subtitle,
  emphasize,
  backgroundColor,
  colorVariant,
  link,
  link2,
  pictureObject,
  pictureAlt,
  text,
  contentIndex,
}: Readonly<HighlightTeaserProps>) {
  const themeName = useThemeName();
  const isMobile = useIsMobile();
  const sectionId = useSectionId(title ?? '', '', false, contentIndex);
  const renderImage = (
    <Image
      format={pictureObject.format}
      data={pictureObject}
      alt={pictureAlt}
      aspectRatio={pictureObject.format}
    />
  );

  return (
    <div
      className={buildClassName(styles[backgroundColor], styles.highlightTeaserWrapper)}
      id={sectionId}
    >
      <Container maxContentWidth="78rem">
        <div
          className={buildClassName(
            styles[colorVariant],
            pictureObject?.url && styles.highlightTeaserContent,
          )}
        >
          <Row>
            <Col size={[4, 3.3, 5]} className={styles.textOnTop}>
              {(title || subtitle) && (
                <Headline
                  className={styles.headlines}
                  format={Formats.h1}
                  tag={Formats.h2}
                  title={title}
                  subtitle={subtitle}
                  titleFontWeight={getFontWeight(emphasize === 'title', themeName, Formats.h1)}
                  subtitleFontWeight={getFontWeight(
                    emphasize === 'subtitle',
                    themeName,
                    Formats.h1,
                  )}
                  titlePreviewId="#st_title"
                  subtitlePreviewId="#st_subtitle"
                />
              )}
              <div className={styles.highlightTeaserText}>
                <InnerHtml
                  as="div"
                  content={text}
                  className={styles.description}
                  previewId="#st_text"
                />
                {link && (
                  <div className={styles.link}>
                    <CmsLink
                      link={link}
                      standardFontSize={false}
                      alignByContent={link.showArrow ? 'left' : undefined}
                    />
                  </div>
                )}
                {link2 && (
                  <div>
                    <CmsLink
                      link={link2}
                      standardFontSize={false}
                      alignByContent={link2.showArrow ? 'left' : undefined}
                    />
                  </div>
                )}
              </div>
              {isMobile && renderImage}
            </Col>
            {!isMobile && renderImage}
          </Row>
        </div>
      </Container>
    </div>
  );
}
